import {
    orderItemsSelector,
    saveApiOrder,
    saveBonificationOrder,
    sendOrder,
    updateOrder,
    addOrderToBonification,
    addTempItems,
    fetchApproversForOrder,
    ApproverOrder,
    clearState as clearSelectItemsState,
    resetBonification,
} from '@redux/slices/order';

import { Checkbox, Col, Form, Popover, Row, Spin } from 'antd';
import React, { memo, useContext, useEffect, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import { SolidBackground } from '@styles/components/wrapper';
import { Button } from '@styles/components/button';
import { ModalContext } from 'src/contexts/modal-context';
import {
    CheckCircleOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Modal } from 'antd';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { message } from 'antd';
import { OrdersBonification } from './orders-bonification-modal';
import { SendCopyRequest } from '@models/order/items/request';
import { FormItem } from '@styles/components/form';
import { Input } from '@components/input';
import { useQuery } from '@hooks/use-query';
import { Prompt } from 'react-router';
import { OrderContext } from '../context/context';
import { clearStateCampaign, selectedCampaignsSelector } from '@redux/slices/campaign';
import { RootState } from '@redux/reducers';
import { Loading } from '@styles/components/loader';
import { ApproversListTimeline } from './approvers-list';
import { translate } from '@components/i18n';
import { ChangeBranch } from './change-branch';
import { ChangeSeller } from './change-seller';
import { authSelector } from '@redux/slices/auth';
import { IS_ARG, IS_BR } from '@config/env';
import { balanceSelector } from '@redux/slices/balance';
import { getPercent } from '@utils/number';

const { confirm } = Modal;

interface SelectItemsActionsProps {
    bonification?: boolean; // its in bonification screen
    marketing?: boolean;
    send?: number;
}

interface Approvers {
    loading: boolean;
    approvers: ApproverOrder[];
}

export const SelectItemsActions: React.FC<SelectItemsActionsProps> = memo(
    ({ bonification = false, marketing = false, send = 0 }) => {
        const selectItemsState = useSelector(orderItemsSelector);
        const balanceState = useSelector(balanceSelector)
        const auth = useSelector(authSelector)
        const query = useQuery();
        const store = useStore<RootState>();
        const modalContext = useContext(ModalContext);
        const history = useHistory();
        const [form] = Form.useForm<SendCopyRequest>();
        const selectedCampaigns = useSelector(selectedCampaignsSelector);
        const dispatch = useDispatch();
        const [sendCopyState, setSendCopyState] = useState<SendCopyRequest>({
            email: '',
            note: '',
            noteNF: '',
            envioProximoPedidoVenda: true,
        });
        const { canLeave, setCanLeave } = useContext(OrderContext);
        const [approvers, setApprovers] = useState<Approvers>({
            loading: true,
            approvers: [],
        });

        const bonificationId = selectItemsState?.orderApi?.pedidoBonificacaoId
        const hasBonification = !!bonificationId
        const isProgrammedOrder = selectItemsState?.order?.orderType?.date
        const isDraft = query.get('draft') === 'true'
        const hasItems = selectItemsState.availableItems.some(i => i.quantity > 0)

        const showObservationNF = IS_ARG // show only for Argentina -> not show for now

        const onOk = (): void => {
            dispatch(
                updateOrder({
                    sendCopy: sendCopyState,
                }),
            );
        };

        useEffect(() => {
            if (selectItemsState.order?.sendCopy) {
                setSendCopyState(selectItemsState.order.sendCopy);
                form.setFieldsValue(selectItemsState.order.sendCopy);
            } else onOk();
        }, []);

        const onChangeValues = (
            changedValues: Partial<SendCopyRequest>,
            allValues: SendCopyRequest,
        ): void => {
            setSendCopyState({
                ...allValues,
                ...changedValues,
            });

            if (changedValues.envioProximoPedidoVenda !== undefined) {
                dispatch(
                    updateOrder({
                        sendCopy: {
                            ...allValues,
                            ...changedValues,
                        },
                    }),
                );
            }
        };

        const cancelOrder = (): void => {
            confirm({
                title:
                    bonification && !marketing
                        ? translate('general.wantToCancelBonificationAndRetorn')
                        : selectItemsState.order.orderType.date
                            ? `${translate('general.wantToCancel')}${selectItemsState.draftRef
                                ? translate('general.edition')
                                : translate('general.creation')
                            } ${translate('general.toprogramming')}`
                            : `${translate('general.wantToCancel')} ${selectItemsState.draftRef
                                ? translate('general.edition')
                                : translate('general.creation')
                            } ${translate('general.toOrder')}`,
                icon: <ExclamationCircleOutlined />,
                className: 'confirm-modal-styled',
                okText: translate('general.no'),
                okType: 'danger',
                cancelText: translate('general.yes'),
                okButtonProps: { type: 'primary' },
                cancelButtonProps: { type: 'primary', className: 'green-btn' },
                onOk() {
                    console.debug('OK');
                },
                onCancel() {
                    if (bonification && !marketing) {
                        // SelectItems é o mesmo componente do pedido de venda;
                        // vindo da bonificação o Redux global fica contaminado.
                        // Limpa o estado dos slices envolvidos ANTES de navegar
                        // para que SelectItems remonte com state fresco e dispare
                        // o fetchOrder do draft do zero.
                        dispatch(clearSelectItemsState());
                        dispatch(resetBonification());
                        dispatch(clearStateCampaign());
                        setCanLeave(() =>
                            history.push(
                                `/create-order/select-items?draft=true&ref=${query.get(
                                    'draftRef',
                                )}`,
                            ),
                        );
                    } else {
                        setCanLeave(() => history.push('/'));
                    }
                },
            });
        };

        const saveOrderDraft = () => {
            const draftRef = selectItemsState.draftRef ? selectItemsState.draftRef : `${new Date().valueOf()}`;

            message.loading({
                content: translate('general.savingDraft'),
                key: draftRef,
                duration: 10,
                className: 'message-styled',
            });

            dispatch(
                saveApiOrder({
                    data: {
                        requestOrder: {
                            ...selectItemsState.order,
                            codigoMoeda: selectItemsState.selectedMoeda.codigo,
                            availableItems:
                                selectItemsState.availableItems.filter(
                                    (i) => i.quantity > 0,
                                ),
                            key: `${new Date().valueOf()}`,
                        },
                    },
                    notificationKey: draftRef,
                    onSuccess: () => {
                        setCanLeave(() => history.push('/'));
                    },
                }),
            );
        }

        const saveCommercialDraft = () => {
            const draftRef = selectItemsState.draftRef ? selectItemsState.draftRef : `${new Date().valueOf()}`;

            message.loading({
                content: translate('general.savingDraft'),
                key: draftRef,
                duration: 10,
                className: 'message-styled',
            });

            dispatch(
                saveBonificationOrder({
                    notificationKey: draftRef,
                    data: {
                        bonificationId: query.get('bonificationId')
                            ? Number(query.get('bonificationId'))
                            : undefined,
                    },
                    onSuccess: () => {
                        setCanLeave(() => history.push('/'));
                    },
                }),
            );
        }

        const saveMarketingDraft = () => {
            const draftRef = selectItemsState.draftRef ? selectItemsState.draftRef : `${new Date().valueOf()}`;
            message.loading({
                content: translate('general.savingDraft'),
                key: draftRef,
                duration: 10,
                className: 'message-styled',
            });

            dispatch(
                saveApiOrder({
                    data: {
                        requestOrder: {
                            ...selectItemsState.order,
                            codigoMoeda: selectItemsState.selectedMoeda.codigo,
                            availableItems:
                                selectItemsState.availableItems.filter(
                                    (i) => i.quantity > 0,
                                ),
                            key: `${new Date().valueOf()}`,
                        },
                    },
                    notificationKey: draftRef,
                }),
            );
        }

        const checkApprovers = async () => {
            setApprovers({ loading: true, approvers: [] });
            const approver = await fetchApproversForOrder(
                store.getState(),
                query.get('draftRef'),
            );
            setApprovers({ loading: false, approvers: approver });
        };

        const draft = () => {
            if (bonification || marketing) {
                saveDraft()
                return
            }

            saveOrder(true)
        }

        const saveDraft = (bonificate?: boolean, fromSaveOrder?: boolean): void => {
            const draftRef = selectItemsState.draftRef ? selectItemsState.draftRef : `${new Date().valueOf()}`;

            if (!fromSaveOrder) {
                message.loading({
                    content: translate('general.savingDraft'),
                    key: draftRef,
                    duration: 10,
                    className: 'message-styled',
                });
            }

            if (bonification && marketing) { // marketing bonification screen
                dispatch(
                    saveApiOrder({
                        data: {
                            requestOrder: {
                                ...selectItemsState.order,
                                codigoMoeda: selectItemsState.selectedMoeda.codigo,
                                availableItems:
                                    selectItemsState.availableItems.filter(
                                        (i) => i.quantity > 0,
                                    ),
                                key: `${new Date().valueOf()}`,
                            },
                            save: false,
                            bonificate,
                        },
                        notificationKey: draftRef,
                        onSuccess: () => {
                            if (!bonificate) {
                                setCanLeave(() => history.push('/'));
                            }
                        },
                    }),
                );
                return
            }

            if (bonification && !marketing) { // commercial bonification screen
                dispatch(
                    saveBonificationOrder({
                        notificationKey: draftRef,
                        data: {
                            bonificationId: query.get('bonificationId')
                                ? Number(query.get('bonificationId'))
                                : undefined,
                        },
                        onSuccess: () => {
                            setCanLeave(() => history.push('/'));
                        },
                    }),
                );
                return
            }

            if (fromSaveOrder) {
                dispatch(
                    saveApiOrder({
                        data: {
                            requestOrder: {
                                ...selectItemsState.order,
                                codigoMoeda: selectItemsState.selectedMoeda.codigo,
                                availableItems:
                                    selectItemsState.availableItems.filter(
                                        (i) => i.quantity > 0,
                                    ),
                                key: `${new Date().valueOf()}`,
                            },
                            save: false,
                            bonificate: bonificate && !selectItemsState.orderApi,
                        },
                        notificationKey: draftRef,
                        onSuccess: () => {
                            if (!bonificate) {
                                setCanLeave(() => history.push('/'));
                            }
                        },
                    }),
                );
            }
        };

        // ao editar pedido com bono, deve editar pedido e aparece 2 botoes
        //   * Salvar rascunho - Salvar apenas o pedido de venda
        //   * Editar Bono - Deve abrir a tela de bonificação para editar e enviar pedido
        //   * Alterar label do botao de Editar Bonificação - Editar bonificação e enviar
        const saveOrder = (notSend?: boolean): void => {
            if (bonification && marketing) { // marketing bonification screen

                if (!verifyMarketingBonificationMax()) return

                const orderKey = `${new Date().valueOf()}`;

                message.loading({
                    content: translate('general.sendingMarketingBonitication'),
                    key: orderKey,
                    duration: 10,
                    className: 'message-styled',
                });

                dispatch(
                    saveApiOrder({
                        data: {
                            requestOrder: {
                                ...selectItemsState.order,
                                codigoMoeda: selectItemsState.selectedMoeda.codigo,
                                availableItems:
                                    selectItemsState.availableItems.filter(
                                        (i) => i.quantity > 0,
                                    ),
                                key: `${new Date().valueOf()}`,
                            },
                            save: notSend ? false : !isProgrammedOrder, // se nao for programada, pode enviar
                        },
                        notificationKey: orderKey,
                        onSuccess: () => {
                            setCanLeave(() => history.push('/'));
                        },
                    }),
                );
                return
            }

            if (bonification) {// commercial bonification screen
                dispatch(
                    saveBonificationOrder({
                        notificationKey: 'bonification',
                        data: {
                            bonificationId: query.get('bonificationId')
                                ? Number(query.get('bonificationId'))
                                : undefined,
                        },
                        onSuccess: () => {
                            if (send !== 0)
                                dispatch(
                                    sendOrder({
                                        data: [send],
                                        notificationKey: 'orderSend',
                                    }),
                                );
                            setCanLeave(() => history.push('/'));
                        },
                    }),
                );
                return
            }

            // save sale order (normal order)
            if (!hasBonification) {
                const bonification = selectedCampaigns
                    ? selectedCampaigns.some((c) => c.tipo.includes('BONIFICACAO'))
                    : false;

                if (!bonification)
                    confirm({
                        title: translate(
                            'general.wantCreateBonificationForThisOrder',
                        ),
                        icon: <CheckCircleOutlined />,
                        className: 'confirm-modal-styled',
                        okText: translate('general.yes'),
                        cancelText: translate('general.no'),
                        cancelButtonProps: {
                            type: 'primary',
                            className: 'dark-blue-btn',
                        },
                        okButtonProps: {
                            type: 'primary',
                            className: 'green-btn',
                        },
                        closable: true,
                        onOk() {
                            saveDraft(true, true);

                            modalContext.setIsOpen(
                                true,
                                <OrdersBonification draft={!!notSend} />,
                                {
                                    title: translate(
                                        'general.comercialBonification',
                                    ),
                                    width: '50%',
                                },
                            );
                        },
                        onCancel(e) {
                            if (e.triggerCancel) return;
                            const orderKey = `${new Date().valueOf()}`;
                            message.loading({
                                content: translate('general.sendingOrder'),
                                key: orderKey,
                                duration: 10,
                                className: 'message-styled',
                            });

                            dispatch(
                                saveApiOrder({
                                    data: {
                                        requestOrder: {
                                            ...selectItemsState.order,
                                            codigoMoeda: selectItemsState.selectedMoeda.codigo,
                                            availableItems:
                                                selectItemsState.availableItems.filter(
                                                    (i) => i.quantity > 0,
                                                ),
                                            key: `${new Date().valueOf()}`,
                                        },
                                        save: notSend ? false : !isProgrammedOrder,
                                    },
                                    notificationKey: orderKey,
                                    onSuccess: () => {
                                        setCanLeave(() =>
                                            history.push('/'),
                                        );
                                    },
                                }),
                            );

                            e();
                        },
                    });
                else {
                    saveDraft(true, true);
                    modalContext.setIsOpen(
                        true,
                        <OrdersBonification draft={!!notSend} />,
                        {
                            title: translate(
                                'general.comercialBonification',
                            ),
                            width: '50%',
                        },
                    );
                }
            } else if (hasBonification) {
                saveDraft(true, true);
                dispatch(
                    addOrderToBonification({
                        ...selectItemsState.order,
                        key: selectItemsState.draftRef,
                        availableItems:
                            selectItemsState.availableItems.filter(
                                (i) => i.quantity > 0,
                            ),
                    }),
                );

                dispatch(
                    addTempItems(selectItemsState.order.bonificationItems),
                );

                modalContext.handleClose();

                if (notSend) {
                    setCanLeave(() =>
                        history.push('/'),
                    );
                    return
                }
                setCanLeave(() =>
                    history.push(
                        `/order/bonification?draftRef=${selectItemsState.draftRef}&bonificationId=${bonificationId}&draft=${!!notSend}`,
                    ),
                );

            }
        };

        const verifyMarketingBonificationMax = (): boolean => {
            if (!bonification || !marketing || !IS_BR || !balanceState.useBalance) return true

            // verifica se a bonificacao esta usando o saldo da liga
            // se tiver, verificar se a quantidade do item nao ultrapassa o limite
            // se ultrapassar, deve exibir a notificacao.

            for (const item of selectItemsState.availableItems) {
                if (item.quantity > 0) {
                    const marca = item.marca
                    const total = selectItemsState.availableItems
                        .filter(it => it.marca === marca)
                        .reduce((acc, it) => acc + (it.total), 0)
                    if (
                        typeof item.limiteBonificacaoMarketing === 'number'
                        && getPercent(balanceState?.balance?.saldoTotalDisponivelCliente, total) > item.limiteBonificacaoMarketing
                    ) {
                        message.error({
                            content: `Item ${item.description} está com valor acima do máximo permitido para Bonificação de Markenting usando o saldo.`,
                            duration: 3,
                            className: 'message-styled-error'
                        });
                        return false
                    }
                }
            }

            const totalOrder = selectItemsState.availableItems
                .filter(it => it.quantity > 0)
                .reduce((acc, it) => acc + (it.total), 0)

            if (
                totalOrder > balanceState?.balance?.saldoTotalDisponivelCliente
            ) {
                message.error({
                    content: `A soma dos itens está acima do máximo permitido para Bonificação de Markenting usando o saldo.`,
                    duration: 3,
                    className: 'message-styled-error'
                });
                return false
            }

            return true;
        }

        useEffect(() => {
            verifyMarketingBonificationMax()
        }, [selectItemsState.availableItems])

        return (
            <SolidBackground
                marginTop={20}
                marginBottom={80}
                paddingBottom={10}
                paddingX={'40px'}
            >
                <Prompt
                    when={!query.get('readOnly') && !canLeave}
                    message={`${translate('general.wantExitOf')} ${query.get('draft')
                        ? translate('general.edition')
                        : translate('general.creation')
                        } ${translate('general.toOrder')}`}
                />
                <Row justify="space-between" align="middle">
                    <Col span={10}>
                        {(marketing || !bonification) && (
                            <Form<SendCopyRequest>
                                onValuesChange={onChangeValues}
                                layout={!marketing ? 'inline' : 'vertical'}
                                labelCol={{ span: 4 }}
                                wrapperCol={{ span: 24 }}
                                initialValues={{ ...sendCopyState }}
                                form={form}
                                style={{ width: '100%' }}
                            >
                                {marketing && (
                                    <>
                                        <FormItem
                                            label={translate(
                                                'forms.labels.sendOrderWithNextSale',
                                            )}
                                            name={'envioProximoPedidoVenda'}
                                            valuePropName="checked"
                                        >
                                            <Checkbox />
                                        </FormItem>
                                        <Row wrap={false}>
                                            <FormItem
                                                label={translate(
                                                    'forms.labels.sendCopyFor',
                                                )}
                                                vertical
                                                style={{
                                                    width: showObservationNF ? '30%' : '40%',
                                                    marginRight: 10,
                                                    flexWrap: 'unset',
                                                }}
                                                name={'email'}
                                                rules={[
                                                    {
                                                        type: 'email',
                                                        message: translate(
                                                            'forms.rules.validEmail',
                                                        ),
                                                    },
                                                ]}
                                            >
                                                <Input
                                                    placeholder={translate(
                                                        'forms.placeholders.email',
                                                    )}
                                                    onBlur={onOk}
                                                />
                                            </FormItem>
                                            <FormItem
                                                label={`${translate(
                                                    'forms.labels.observation',
                                                )}:`}
                                                name={'note'}
                                                vertical
                                                style={{
                                                    width: showObservationNF ? '30%' : '40%',
                                                    flexWrap: 'unset',
                                                }}
                                            >
                                                <Input
                                                    placeholder={translate(
                                                        'forms.labels.observation',
                                                    )}
                                                    onBlur={onOk}
                                                />
                                            </FormItem>
                                            {showObservationNF && (
                                                <FormItem
                                                    label={`${translate(
                                                        'forms.labels.observationNF',
                                                    )}:`}
                                                    name={'noteNF'}
                                                    vertical
                                                    style={{
                                                        width: showObservationNF ? '30%' : '50%',
                                                        flexWrap: 'unset',
                                                    }}
                                                >
                                                    <Input
                                                        placeholder={translate(
                                                            'forms.labels.observationNF',
                                                        )}
                                                        onBlur={onOk}
                                                    />
                                                </FormItem>
                                            )}
                                        </Row>
                                    </>
                                )}
                                {!marketing && (
                                    <>
                                        <FormItem
                                            label={translate(
                                                'forms.labels.sendCopyFor',
                                            )}
                                            name={'email'}
                                            style={{ width: showObservationNF ? '30%' : '40%' }}
                                            rules={[
                                                {
                                                    type: 'email',
                                                    message: translate(
                                                        'forms.rules.validEmail',
                                                    ),
                                                },
                                            ]}
                                        >
                                            <Input
                                                placeholder={translate(
                                                    'forms.placeholders.email',
                                                )}
                                                onBlur={onOk}
                                            />
                                        </FormItem>
                                        <FormItem
                                            label={`${translate(
                                                'forms.labels.observation',
                                            )}:`}
                                            name={'note'}
                                            style={{ width: showObservationNF ? '30%' : '40%' }}
                                        >
                                            <Input
                                                placeholder={translate(
                                                    'forms.labels.observation',
                                                )}
                                                onBlur={onOk}
                                            />
                                        </FormItem>
                                        {showObservationNF && (
                                            <FormItem
                                                label={`${translate(
                                                    'forms.labels.observationNF',
                                                )}:`}
                                                name={'noteNF'}
                                                style={{
                                                    width: showObservationNF ? '30%' : '40%',
                                                    flexWrap: 'unset',
                                                }}
                                            >
                                                <Input
                                                    placeholder={translate(
                                                        'forms.labels.observationNF',
                                                    )}
                                                    onBlur={onOk}
                                                />
                                            </FormItem>
                                        )}
                                    </>
                                )}
                            </Form>
                        )}
                    </Col>
                    <Row gutter={5}>
                        {IS_ARG && selectItemsState?.orderApi?.pedidoCopia && (
                            <>
                                <Col>
                                    <ChangeBranch />
                                </Col>
                                {auth.isAdmin && (
                                    <Col>
                                        <ChangeSeller />
                                    </Col>
                                )}
                            </>
                        )}
                        <Col>
                            <Button
                                danger
                                type="primary"
                                height={40}
                                onClick={cancelOrder}
                            >
                                {translate('general.cancel')}
                            </Button>
                        </Col>
                        <Col>
                            <Popover
                                content={() => {
                                    return (
                                        <>
                                            {approvers.loading && <Loading />}
                                            {!approvers.loading &&
                                                approvers.approvers.length ===
                                                0 && (
                                                    <span>
                                                        {translate(
                                                            'general.noDetectedApprover',
                                                        )}
                                                    </span>
                                                )}
                                            {!approvers.loading &&
                                                approvers.approvers.length >
                                                0 && (
                                                    <ApproversListTimeline
                                                        approvers={
                                                            approvers.approvers
                                                        }
                                                    />
                                                )}
                                        </>
                                    );
                                }}
                                trigger="click"
                                onVisibleChange={(visible) => {
                                    if (visible) checkApprovers();
                                }}
                            >
                                <Button
                                    type="primary"
                                    height={40}
                                    disabled={!hasItems}
                                    lightBlue
                                >
                                    {translate('general.approvers')}
                                </Button>
                            </Popover>
                        </Col>

                        {/* BOTAO AZUL ESCURO - SALVAR DRAFT */}
                        {(!isProgrammedOrder && (
                            isDraft ||
                            !isDraft && !selectItemsState.orderApi && !bonification ||
                            marketing && !selectItemsState.orderApi
                        )) && (
                                <Col>
                                    <Button
                                        type="primary"
                                        darkBlue
                                        height={40}
                                        onClick={draft}
                                        disabled={!hasItems || selectItemsState.isSubmitting}
                                    >
                                        {translate('general.saveDraft')}
                                    </Button>
                                </Col>
                            )}

                        {/* BOTAO AZUL - SEND OU EDIT */}
                        {isDraft && hasBonification && (
                            <Col>
                                <Button
                                    type="primary"
                                    height={40}
                                    disabled={!!selectItemsState.canSaveReason || !hasItems}
                                    onClick={() => saveOrder()}
                                >
                                    {translate('general.editBoniticationAndSend')}
                                </Button>
                            </Col>
                        )}
                        {((!hasBonification && !bonification) || (!isDraft && bonification) || (isDraft && bonification && marketing)) && (
                            <Col>
                                <Button
                                    type="primary"
                                    height={40}
                                    disabled={!!selectItemsState.canSaveReason || !hasItems || selectItemsState.isSubmitting}
                                    onClick={() => saveOrder()}
                                >
                                    {translate('general.send')}
                                </Button>
                            </Col>
                        )}
                    </Row>
                </Row>
            </SolidBackground>
        );
    },
);
